/**
 * consumptionService — authFetch wrappers around /api/tenant/me/capabilities/.
 *
 * WQ-231 (Phase 4.9 α5). Tenant-scoped read-only mirror of the operator
 * allocationAdminService. Tenant scope is JWT-derived on the backend; the
 * frontend never sends a tenant_id parameter on these calls.
 *
 *   GET  /api/tenant/me/capabilities/                    — listMyCapabilities
 *   GET  /api/tenant/me/capabilities/{capability_id}     — getMyCapability
 *   GET  /api/tenant/me/capabilities/{capability_id}/usage — getMyCapabilityUsage
 *
 * The response shapes match `allocationAdminService` so the AllocationsTab
 * component renders identically when this service is passed via the
 * `serviceOverride` prop in readOnly mode.
 */

import type {
  Allocation,
  UsageAggregate,
  AllocationFilters,
} from './allocationAdminService';

// Re-export so tenant-side consumers can import types from one place.
export type { Allocation, UsageAggregate, AllocationFilters };

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/tenant/me/capabilities';

/** List allocations granted to the calling tenant.
 * The backend ignores any `tenant_id` filter; we forward only the
 * filters that the tenant endpoint accepts (status, capability_type).
 */
export async function listMyCapabilities(
  authFetch: AuthFetch,
  filters: AllocationFilters = {},
): Promise<Allocation[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.capability_type) params.set('capability_type', filters.capability_type);
  const qs = params.toString();
  const url = qs ? `${BASE}?${qs}` : BASE;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`listMyCapabilities failed: ${res.status}`);
  return res.json();
}

export async function getMyCapability(
  authFetch: AuthFetch,
  capabilityId: string,
): Promise<Allocation> {
  const res = await authFetch(`${BASE}/${capabilityId}`);
  if (!res.ok) throw new Error(`getMyCapability failed: ${res.status}`);
  return res.json();
}

export async function getMyCapabilityUsage(
  authFetch: AuthFetch,
  capabilityId: string,
  periodStart?: string,
): Promise<UsageAggregate> {
  const url = periodStart
    ? `${BASE}/${capabilityId}/usage?period_start=${encodeURIComponent(periodStart)}`
    : `${BASE}/${capabilityId}/usage`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`getMyCapabilityUsage failed: ${res.status}`);
  return res.json();
}

/**
 * AllocationsTab/Drawer-shaped service module — pass to the components'
 * `serviceOverride` prop. Method names mirror the admin service so the
 * components are agnostic to which backend they hit.
 */
export const consumptionService = {
  listAllocations: listMyCapabilities,
  getAllocationUsage: getMyCapabilityUsage,
};
